import * as React from 'react';
import { storyblokEditable, StoryblokComponent } from '@storyblok/react';
import type { SbBlokData } from '@storyblok/react';

import type { CardGridStoryblok } from '../../types/storyblok';
import ornament4Src from '../../images/board/ornament-4.svg';
import ornament5Src from '../../images/board/ornament-5.svg';
import blueShadeSrc from '../../images/board/blue-shade.svg';

interface CardGridProps {
  blok: CardGridStoryblok;
}

type Background = 'default' | 'muted' | 'accent';
type Columns = '2' | '3' | '4';

const backgroundClass: Record<Background, string> = {
  default: '',
  muted: 'bg-gray-50',
  accent: 'bg-secondary-iceBlue',
};

const columnsClass: Record<Columns, string> = {
  '2': 'desktop:grid-cols-2',
  '3': 'desktop:grid-cols-3',
  '4': 'desktop:grid-cols-4',
};

/**
 * Normalizes background value to a valid Background type.
 * Defaults to 'default' when not specified or invalid.
 */
const normalizeBackground = (value: CardGridStoryblok['background']): Background =>
  value === 'muted' || value === 'accent' ? value : 'default';

/**
 * Normalizes columns value to a valid Columns type.
 * Defaults to '3' when not specified or invalid.
 */
const normalizeColumns = (value: CardGridStoryblok['columns']): Columns =>
  value === '2' || value === '3' || value === '4' ? value : '3';

/**
 * Resolves the href from a Storyblok multilink field.
 * Prefers cached_url over url.
 */
const resolveCtaHref = (link: CardGridStoryblok['cta_link']): string | null => {
  if (!link) return null;
  const href = link.cached_url || link.url;
  return href || null;
};

export const CardGrid: React.FC<CardGridProps> = ({ blok }) => {
  const headlineId = React.useId();
  const background = normalizeBackground(blok.background);
  const columns = normalizeColumns(blok.columns);
  const ctaHref = resolveCtaHref(blok.cta_link);
  const ctaLabel = blok.cta_label?.trim();
  const showCta = Boolean(ctaHref && ctaLabel);
  const cards = blok.cards ?? [];

  const sectionClass = [
    'px-4 py-10 desktop:px-grid-margin-desktop desktop:py-[189px]',
    'relative overflow-hidden',
    backgroundClass[background],
  ]
    .filter(Boolean)
    .join(' ');

  const gridClass = ['grid grid-cols-1 gap-10', columnsClass[columns]].join(' ');

  return (
    <section
      {...storyblokEditable(blok as unknown as SbBlokData)}
      aria-labelledby={headlineId}
      className={sectionClass}
    >
      {/* Decorative ornaments - hidden on mobile */}
      <CardGridDecorations />

      {/* Content container */}
      <div className="relative max-w-[1110px] mx-auto flex flex-col items-center gap-[54px] desktop:gap-20">
        {/* Header section */}
        <div className="flex flex-col items-center gap-4 text-center">
          {blok.eyebrow && (
            <p className="text-callout font-extrabold uppercase tracking-[2.24px] text-brand-deep">
              {blok.eyebrow}
            </p>
          )}
          <h2
            id={headlineId}
            className="text-h5 desktop:text-h4 font-extrabold text-brand-mid text-center"
          >
            {blok.headline}
          </h2>
        </div>

        {/* Cards grid */}
        {cards.length > 0 && (
          <div className={gridClass}>
            {cards.map((card) => (
              <StoryblokComponent key={card._uid} blok={card as unknown as SbBlokData} />
            ))}
          </div>
        )}

        {/* CTA button - secondary/outlined style per Figma */}
        {showCta && (
          <a
            href={ctaHref ?? undefined}
            className="inline-flex items-center justify-center gap-2 px-8 py-[14px] rounded-[4px] border border-brand-deep bg-transparent text-brand-deep font-medium transition-colors hover:bg-brand-deep hover:text-neutral-white"
          >
            {ctaLabel}
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M3.33337 8H12.6667"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8 3.33337L12.6667 8.00004L8 12.6667"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        )}
      </div>
    </section>
  );
};

/**
 * Decorative elements for the CardGrid section.
 * Hidden on mobile (below desktop breakpoint), visible on desktop.
 * Includes: blue radial gradient, and two dot ornaments.
 *
 * Assets are imported from local SVGs; previous Figma MCP asset URLs were
 * ephemeral and expired, which the designer flagged as a rendering issue.
 */
const CardGridDecorations: React.FC = () => (
  <span aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
    {/* Blue shade - bottom right radial gradient */}
    <img
      src={blueShadeSrc}
      alt=""
      className="absolute hidden desktop:block w-[462px] h-[462px] object-contain"
      style={{ right: '-50px', bottom: '50px' }}
    />

    {/* Ornament dots - top right (rotated, mirrored) */}
    <img
      src={ornament4Src}
      alt=""
      className="absolute hidden desktop:block w-[256px] h-[256px]"
      style={{ right: '-30px', top: '-30px', transform: 'scaleY(-1) rotate(-142deg)' }}
    />

    {/* Ornament dots - left side (rotated, mirrored) */}
    <img
      src={ornament5Src}
      alt=""
      className="absolute hidden desktop:block w-[256px] h-[256px]"
      style={{ left: '-130px', top: '350px', transform: 'scaleY(-1) rotate(-47deg)' }}
    />
  </span>
);
